// Updated ProgressContext.tsx
// This version supports BOTH authenticated users (Firebase) and guest users (localStorage)
// Replace your existing ProgressContext.tsx with this file

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

interface Achievement {
  id: string;
  title: string;
  unlocked: boolean;
  icon: string;
  description: string;
}

interface CompletedChallenge {
  completed: boolean;
  completedAt: Date;
  xpAwarded: number;
  storyId?: string;
}

interface ProgressContextType {
  xp: number;
  level: number;
  streak: number;
 
  totalWordCount: number;
  chaptersWrittenThisWeek: number;
  achievements: Achievement[];
  completedChallenges: Record<string, CompletedChallenge>;
  addXP: (amount: number) => void;
  addWords: (count: number) => void;
  incrementChaptersThisWeek: () => void;
  unlockAchievement: (id: string) => void;
  updateStreak: () => void;
  completeChallenge: (challengeType: 'daily' | 'weekly', xpReward: number, storyId?: string) => boolean;
  isChallengeCompleted: (challengeType: 'daily' | 'weekly') => boolean;
  loading: boolean;
  isGuestMode: boolean; // NEW: Flag to check if user is guest
  getProgressSummary: () => string; // NEW: For registration prompts
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

interface ProgressProviderProps {
  children: ReactNode;
}

// ============================================================================
// GUEST PROGRESS - localStorage utilities
// ============================================================================
const GUEST_STORAGE_KEY = 'scriblish_guest_progress';

interface GuestProgressData {
  xp: number;
  level: number;
  streak: number;
  lastWriteDate: string | null;
  totalWordCount: number;
  chaptersWrittenThisWeek: number;
  achievements: Achievement[];
  completedChallenges: Record<string, any>;
}

const loadGuestProgress = (): GuestProgressData | null => {
  const stored = localStorage.getItem(GUEST_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const saveGuestProgress = (data: GuestProgressData): void => {
  localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
};

const clearGuestProgress = (): void => {
  localStorage.removeItem(GUEST_STORAGE_KEY);
};

// ============================================================================
// MIGRATION - Move guest progress to Firebase after registration
// ============================================================================
export const migrateGuestProgressToUser = async (userId: string): Promise<void> => {
  try {
    const guestData = loadGuestProgress();
    
    // Import guest story utilities
    const { getGuestStories, clearGuestStories } = await import('../utils/guestStoryStorage');
    const guestStories = getGuestStories();
    
    if ((!guestData || guestData.xp === 0) && guestStories.length === 0) {
      console.log('No guest progress to migrate');
      return;
    }

    console.log('🔄 Migrating guest data to user:', userId);
    console.log('- Progress:', guestData);
    console.log('- Stories:', guestStories.length);

    // 1. Migrate progress (XP, streak, etc.)
    if (guestData) {
      const progressRef = doc(db, 'users', userId, 'progress', 'current');
      
      await setDoc(progressRef, {
        xp: guestData.xp,
        level: guestData.level,
        streak: guestData.streak,
        totalWordCount: guestData.totalWordCount,
        chaptersWrittenThisWeek: guestData.chaptersWrittenThisWeek,
        achievements: guestData.achievements,
        completedChallenges: guestData.completedChallenges,
        lastWriteDate: guestData.lastWriteDate,
        migratedFromGuest: true,
        migrationDate: new Date(),
        createdAt: new Date(),
        lastUpdated: new Date()
      });
      
      console.log('✅ Progress migrated');
    }

    // 2. Migrate stories
    if (guestStories.length > 0) {
      const { collection: firestoreCollection } = await import('firebase/firestore');
      
      for (const guestStory of guestStories) {
        try {
          // Generate new Firebase story ID
          const storyRef = doc(firestoreCollection(db, 'users', userId, 'stories'));
          
          // Save story with new ID
          await setDoc(storyRef, {
            title: guestStory.title,
            type: guestStory.type,
            thumbnail: guestStory.thumbnail,
            content: guestStory.content,
            planData: guestStory.planData,
            createdAt: guestStory.createdAt,
            updatedAt: new Date(),
            migratedFromGuest: true,
            originalGuestId: guestStory.id
          });
          
          console.log('✅ Story migrated:', guestStory.title);
        } catch (error) {
          console.error('❌ Error migrating story:', guestStory.title, error);
        }
      }
      
      console.log(`✅ All ${guestStories.length} stories migrated`);
    }

    // 3. Clear localStorage after successful migration
    clearGuestProgress();
    if (guestStories.length > 0) {
      clearGuestStories();
    }
    
    console.log('✅ Guest data migration complete');
    
  } catch (error) {
    console.error('❌ Error migrating guest data:', error);
    throw error;
  }
};

// ============================================================================
// MAIN PROVIDER
// ============================================================================
export const ProgressProvider: React.FC<ProgressProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const isGuestMode = !currentUser;
  
  // State
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [streak, setStreak] = useState(0);
  const [lastWriteDate, setLastWriteDate] = useState<string | null>(null);
  const [totalWordCount, setTotalWordCount] = useState(0);
  const [chaptersWrittenThisWeek, setChaptersWrittenThisWeek] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completedChallenges, setCompletedChallenges] = useState<Record<string, CompletedChallenge>>({});
  const [achievements, setAchievements] = useState<Achievement[]>([
    { id: 'first-story', title: 'First Story', unlocked: false, icon: '⭐', description: 'Complete your first story' },
    { id: '7-day-streak', title: '7 Day Streak', unlocked: false, icon: '🔥', description: 'Write for 7 days in a row' },
    { id: '1000-words', title: '1000 Words', unlocked: false, icon: '✍️', description: 'Write 1000 total words' },
    { id: 'first-drawing', title: 'First Drawing', unlocked: false, icon: '🎨', description: 'Add your first illustration' },
    { id: 'finished-book', title: 'Finished a Book', unlocked: false, icon: '📚', description: 'Complete a full story' },
    { id: 'golden-pen', title: 'Golden Pen', unlocked: false, icon: '🖊️', description: 'Write 2 chapters in a week' }
  ]);

  // Guard to prevent duplicate loads
  const hasLoadedRef = useRef(false);
  const loadingRef = useRef(false);

  // Helper: Get today's date string (YYYY-MM-DD)
  const getTodayDateString = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper: Get current week string (YYYY-Www)
  const getCurrentWeekString = (): string => {
    const today = new Date();
    const onejan = new Date(today.getFullYear(), 0, 1);
    const week = Math.ceil((((today.getTime() - onejan.getTime()) / 86400000) + onejan.getDay() + 1) / 7);
    return `${today.getFullYear()}-W${week.toString().padStart(2, '0')}`;
  };

  // Helper: Get challenge key
  const getChallengeKey = (challengeType: 'daily' | 'weekly'): string => {
    if (challengeType === 'daily') {
      return `daily-${getTodayDateString()}`;
    } else {
      return `weekly-${getCurrentWeekString()}`;
    }
  };

  // XP System - Exponential leveling (1.4x multiplier per level)
  const getXPForLevel = (level: number): number => {
    if (level <= 1) return 0;
    return Math.floor(500 * Math.pow(1.4, level - 2));
  };

  const getTotalXPForLevel = (level: number): number => {
    let total = 0;
    for (let i = 2; i <= level; i++) {
      total += getXPForLevel(i);
    }
    return total;
  };

  // ============================================================================
  // LOAD DATA - Works for both guest (localStorage) and authenticated (Firebase)
  // ============================================================================
  useEffect(() => {
    console.log('🔍 Load effect triggered. Guest mode:', isGuestMode);
    
    // Guard against duplicate loads
    if (loadingRef.current) {
      console.log('⚠️ Already loading, skipping duplicate');
      return;
    }

    const loadProgress = async () => {
      loadingRef.current = true;
      
      try {
        if (isGuestMode) {
          // GUEST MODE - Load from localStorage
          console.log('👤 Loading guest progress from localStorage...');
          const guestData = loadGuestProgress();
          
          if (guestData) {
            console.log('✅ Guest data loaded:', guestData);
            setXp(guestData.xp);
            setLevel(guestData.level);
            setStreak(guestData.streak);
            setTotalWordCount(guestData.totalWordCount);
            setChaptersWrittenThisWeek(guestData.chaptersWrittenThisWeek);
            setAchievements(guestData.achievements);
            setCompletedChallenges(guestData.completedChallenges);
          } else {
            console.log('ℹ️ No guest data found, starting fresh');
          }
        } else {
          // AUTHENTICATED MODE - Load from Firebase
          console.log('🔐 Loading authenticated user progress from Firebase...');
          const progressRef = doc(db, 'users', currentUser.uid, 'progress', 'current');
          const docSnap = await getDoc(progressRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log('✅ Firebase data loaded:', data);
            
            setXp(data.xp || 0);
            setLevel(data.level || 1);
            setStreak(data.streak || 0);
            setLastWriteDate(data.lastWriteDate || null); 
            setTotalWordCount(data.totalWordCount || 0);
            setChaptersWrittenThisWeek(data.chaptersWrittenThisWeek || 0);
            setAchievements(data.achievements || achievements);
            setCompletedChallenges(data.completedChallenges || {});
          } else {
            console.log('ℹ️ No Firebase data found, creating initial document...');
            const initialData = {
              xp: 0,
              level: 1,
              streak: 0,
              totalWordCount: 0,
              chaptersWrittenThisWeek: 0,
              achievements,
              completedChallenges: {},
              createdAt: new Date(),
              lastUpdated: new Date()
            };
            
            await setDoc(progressRef, initialData);
            console.log('✅ Initial Firebase document created');
          }
        }
        
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('❌ Error loading progress:', error);
      } finally {
        console.log('✅ Loading complete');
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadProgress();
  }, [currentUser, isGuestMode]); // Reload when user logs in/out

  // ============================================================================
  // SAVE DATA - Works for both guest (localStorage) and authenticated (Firebase)
  // ============================================================================
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (loading || !isDirty) {
      return;
    }

    console.log('💾 Saving progress... Guest mode:', isGuestMode);
    
    const timeoutId = setTimeout(async () => {
      try {
        const dataToSave = {
          xp,
          level,
          streak,
          totalWordCount,
          chaptersWrittenThisWeek,
          achievements,
          completedChallenges,
          lastWriteDate: getTodayDateString()
        };

        if (isGuestMode) {
          // GUEST MODE - Save to localStorage
          console.log('💾 Saving to localStorage:', dataToSave);
          saveGuestProgress(dataToSave as GuestProgressData);
        } else {
          // AUTHENTICATED MODE - Save to Firebase
          console.log('💾 Saving to Firebase:', dataToSave);
          const progressRef = doc(db, 'users', currentUser.uid, 'progress', 'current');
          await setDoc(progressRef, {
            ...dataToSave,
            lastUpdated: new Date()
          }, { merge: true });
        }
        
        console.log('✅ Progress saved');
        setIsDirty(false);
      } catch (error) {
        console.error('❌ Error saving progress:', error);
      }
    }, 2000); // Wait 2 seconds before saving

    return () => clearTimeout(timeoutId);
  }, [currentUser, xp, level, streak, totalWordCount, chaptersWrittenThisWeek, achievements, completedChallenges, loading, isDirty, isGuestMode]);

  // Calculate level based on XP
  useEffect(() => {
    let currentLevel = 1;
    let totalXP = 0;
    
    while (totalXP <= xp && currentLevel < 50) {
      currentLevel++;
      totalXP += getXPForLevel(currentLevel);
    }
    
    const newLevel = currentLevel - 1;
    if (newLevel !== level) {
      setLevel(newLevel);
      setIsDirty(true);
    }
  }, [xp]);

  // Weekly reset (only for authenticated users)
  useEffect(() => {
    if (isGuestMode || !currentUser) return;

    const checkWeeklyReset = async () => {
      const today = new Date();
      if (today.getDay() === 1) { // Monday
        try {
          const progressRef = doc(db, 'users', currentUser.uid, 'progress', 'current');
          const docSnap = await getDoc(progressRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            const lastReset = data.lastWeeklyReset?.toDate();
            
            if (!lastReset || today.getDate() !== lastReset.getDate()) {
              await setDoc(progressRef, {
                chaptersWrittenThisWeek: 0,
                lastWeeklyReset: new Date()
              }, { merge: true });
              
              setChaptersWrittenThisWeek(0);
              console.log('♻️ Weekly reset completed');
            }
          }
        } catch (error) {
          console.error('Error checking weekly reset:', error);
        }
      }
    };

    checkWeeklyReset();
    const interval = setInterval(checkWeeklyReset, 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser, isGuestMode]);

  // ============================================================================
  // FUNCTIONS - Work for both guest and authenticated
  // ============================================================================
  const addXP = (amount: number) => {
    
    setXp(prev => prev + amount);
    setIsDirty(true);
  };

  const addWords = (count: number) => {
    
    setTotalWordCount(prev => {
      const newTotal = prev + count;
      
      if (newTotal >= 1000 && !achievements.find(a => a.id === '1000-words')?.unlocked) {
        unlockAchievement('1000-words');
      }
      
      return newTotal;
    });
    addXP(Math.floor(count / 10));
    setIsDirty(true);
  };

  const incrementChaptersThisWeek = () => {
    setChaptersWrittenThisWeek(prev => {
      const newCount = prev + 1;
      if (newCount >= 2) {
        unlockAchievement('golden-pen');
      }
      return newCount;
    });
    addXP(50);
    setIsDirty(true);
  };

  const unlockAchievement = (id: string) => {
    setAchievements(prev => {
      const updated = prev.map(achievement => 
        achievement.id === id ? { ...achievement, unlocked: true } : achievement
      );
      return updated;
    });
    setIsDirty(true);
  };

  const updateStreak = () => {
    console.log('🔥 updateStreak called');
    const today = getTodayDateString();
    
    // Get lastWrite for both guest and authenticated
    let lastWrite;
    if (isGuestMode) {
      const guestData = loadGuestProgress();
      lastWrite = guestData?.lastWriteDate;
    } else {
      // For authenticated users, use the state value
      lastWrite = lastWriteDate; // ← Using state variable
    }
  
    console.log('📅 Streak check:', { today, lastWrite, isGuestMode });
  
    if (!lastWrite) {
      // First time writing
      console.log('✅ First time writing - setting streak to 1');
      setStreak(1);
      setLastWriteDate(today);
      setIsDirty(true);
      return;
    }
  
    // If streak is 0 but has lastWrite, initialize
    if (streak === 0) {
      console.log('✅ Initializing streak to 1 (was 0)');
      setStreak(1);
      setLastWriteDate(today);
      setIsDirty(true);
    }
  
    const previousWriteDate = new Date(lastWrite); // ← CHANGED name from lastWriteDate
    const todayDate = new Date(today);
    const diffTime = todayDate.getTime() - previousWriteDate.getTime(); // ← Use new name
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
    if (diffDays === 0) {
      // Same day - no change
      return;
    } else if (diffDays === 1) {
      // Consecutive day - increment
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak >= 7) {
          unlockAchievement('7-day-streak');
        }
        return newStreak;
      });
      setLastWriteDate(today);
      setIsDirty(true);
    } else {
      // Missed days - reset to 1
      setStreak(1);
      setLastWriteDate(today);
      setIsDirty(true);
    }
  };

  const completeChallenge = (challengeType: 'daily' | 'weekly', xpReward: number, storyId?: string): boolean => {
    const challengeKey = getChallengeKey(challengeType);
    
    if (completedChallenges[challengeKey]?.completed) {
      console.log('⚠️ Challenge already completed:', challengeKey);
      return false;
    }
    
    const challengeData: CompletedChallenge = {
      completed: true,
      completedAt: new Date(),
      xpAwarded: xpReward
    };
    
    if (storyId) {
      challengeData.storyId = storyId;
    }
    
    setCompletedChallenges(prev => ({
      ...prev,
      [challengeKey]: challengeData
    }));
    
    addXP(xpReward);
    setIsDirty(true);
    
    console.log(`✅ Challenge completed: ${challengeKey} (+${xpReward} XP)`);
    return true;
  };

  const isChallengeCompleted = (challengeType: 'daily' | 'weekly'): boolean => {
    const challengeKey = getChallengeKey(challengeType);
    return completedChallenges[challengeKey]?.completed || false;
  };

  // NEW: Get progress summary for registration prompts
  const getProgressSummary = (): string => {
    const parts: string[] = [];
    
    if (xp > 0) {
      parts.push(`${xp} XP`);
    }
    if (streak > 1) {
      parts.push(`${streak}-day streak`);
    }
    if (totalWordCount > 0) {
      parts.push(`${totalWordCount} words`);
    }

    return parts.join(', ') || 'your progress';
  };

  const value: ProgressContextType = {
    xp,
    level,
    streak,
    totalWordCount,
    chaptersWrittenThisWeek,
    achievements,
    completedChallenges,
    addXP,
    addWords,
    incrementChaptersThisWeek,
    unlockAchievement,
    updateStreak,
    completeChallenge,
    isChallengeCompleted,
    loading,
    isGuestMode, // NEW
    getProgressSummary // NEW
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};